import { useCallback, useMemo } from 'react'

import { STANDALONE_PLANE_OPACITY } from '@editor/config/styles'
import { usePrintScale } from '@editor/hooks/usePrintScale'
import { PointArray } from '@editor/interfaces/attribute'
import { PolygonPlaneMeshProps } from '@editor/interfaces/canvas'
import { DrawingParameterPolygon, DrawingType, PolygonSubtype } from '@editor/interfaces/drawing'
import { CanvasConfigV2, CanvasEventsExtra } from '@editor/interfaces/editor'
import { Polygon } from '@editor/interfaces/shape'
import { BlueprintEditorTools, DEFAULT_STYLES, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useDrawingStateCheck from '@editor/pages/projects/blueprint-editor/hooks/useDrawingStateCheck'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { addDrawing, updateDrawingParameters } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { getDrawingsOfType } from '@editor/services/Drawing'
import { findCenter } from '@editor/services/Points'
import { generateDummyMinimumRectBoundary, getPolygonFromPoints } from '@editor/services/Shape'
import { convertPrintDimension } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useToolUtilSelector } from '@editor/store/selectors'
import { setWorkingElementId, toggleSelectedElementIds } from '@editor/store/tool'
import { assign } from 'lodash'
import { Plane, Vector3 } from 'three'

import { createPolygonProjection, getLastCount, getNewOrder } from '../../utils'
import { WORKING_ID } from '../constants'
import { addWorkingPoint, reset, setIsClosing } from '../store'
import useAnchors from './useAnchors'
import useUndo from './useUndo'

export default function useMainCanvas(): CanvasConfigV2 {
  // Hooks
  const { projectionPlane, calculateOffsetMatrix } = useProjectionPlane()
  const { currentPrintScale } = usePrintScale()
  const { editingElementId, isSelectable } = useDrawingStateCheck()
  const { getIntersection } = usePointSelection()

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)

  const hiddenElementIds = useToolUtilSelector((state) => state.hiddenElementIds)
  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)

  const selectedBlueprint = useAppSelector((state) => state.blueprint.editor.selectedBlueprint)
  const selectedTool = useAppSelector((state) => state.blueprint.editor.selectedTool)
  const mode = useAppSelector((state) => state.blueprint.editor.mode)
  const drawings = useAppSelector((state) => state.blueprint.drawings.document.drawings)
  const renderSkipUuids = useAppSelector((state) => state.blueprint.drawings.renderSkipUuids)
  const defaultStyles = useAppSelector((state) => state.blueprint.drawings.defaultStyles)
  const workingStyles = useAppSelector((state) => state.blueprint.drawings.workingStyles)
  const isAllowedToModify = useAppSelector((state) => state.blueprint.editor.isAllowedToModify)

  const workingPoints = useAppSelector((root) => root.bpToolPolygon.workingPoints)
  const isClosing = useAppSelector((root) => root.bpToolPolygon.isClosing)

  // States
  const workingPlane = useMemo(() => {
    // Need at least 1 point
    if (!workingPoints.length) return null

    if (!selectedBlueprint?.sort_key || !selectedBlueprint?.projection_target_plane || !projectionPlane) {
      if (workingPoints.length < 3) return null

      // Fallback to working points. This should never happen in a normal flow.
      return new Plane().setFromCoplanarPoints(
        new Vector3(...workingPoints[0]),
        new Vector3(...workingPoints[1]),
        new Vector3(...workingPoints[2]),
      )
    }

    const plane = projectionPlane.clone()

    // Translate the plane to the first working point.
    if (workingPoints.length) {
      const firstPoint = new Vector3(...workingPoints[0])
      plane.translate(firstPoint.sub(plane.projectPoint(firstPoint, new Vector3())))
    }

    return plane
  }, [workingPoints, projectionPlane, selectedBlueprint])

  // Hooks
  const { anchors } = useAnchors()
  const { handleUndo } = useUndo()

  // Vars
  const isSelectedTool = useMemo(() => selectedTool === BlueprintEditorTools.Polygon, [selectedTool])

  /**
   * Complete polygon creation with current working points
   */
  const completePolygon = useCallback(() => {
    if (workingPoints.length < 3) return

    const points = workingPoints.map((p) => new Vector3(...p))
    const { vertices, matrix } = createPolygonProjection(points)
    const center = findCenter(points)!

    dispatch(
      addDrawing({
        name: `ポリゴン ${
          getLastCount(
            drawings.map((p) => p.name),
            'ポリゴン',
          ) + 1
        }`,
        drawing_type: DrawingType.Polygon,
        drawing_parameters: {
          uuid: crypto.randomUUID(),
          subtype: PolygonSubtype.Polygon,
          points: workingPoints,
          vertices,
          center: center.toArray(),
          transformation: matrix.toArray(),
          order: getNewOrder(drawings),
          styles: assign({}, defaultStyles, workingStyles),
        },
      }),
    )
    dispatch(setIsClosing(false))
    dispatch(reset())
    dispatch(setWorkingElementId(undefined))
  }, [workingPoints, drawings, defaultStyles, workingStyles, dispatch])

  /**
   * Event Handlers (onUp).
   * Will be called by both mouse and touch events.
   */
  const onUp = useCallback(
    (point: PointArray | undefined, { isDoubleClicked, isDragged }: CanvasEventsExtra) => {
      if (isClosing) completePolygon()
      if (
        !point ||
        isDragged ||
        !project ||
        !inspectionArea ||
        !selectedBlueprint?.sort_key ||
        isClosing ||
        editingElementId
      )
        return

      // Double clicked with more than 2 points
      const completesPolygon = workingPoints.length > 2 && isDoubleClicked

      if (completesPolygon) {
        completePolygon()
        return
      }

      if (!isDoubleClicked) {
        dispatch(
          addWorkingPoint(
            workingPoints.length && workingPlane
              ? workingPlane.projectPoint(new Vector3(...point), new Vector3()).toArray()
              : point,
          ),
        )
        dispatch(setWorkingElementId(WORKING_ID))
      }
    },
    [
      isClosing,
      workingPoints,
      workingPlane,
      project,
      inspectionArea,
      selectedBlueprint,
      editingElementId,
      completePolygon,
      dispatch,
    ],
  )

  return {
    objects: {
      circleAnchors: anchors,
      polygonPlaneMeshesV2: [
        /**
         * Saved planes
         */
        ...useMemo(
          () =>
            getDrawingsOfType<DrawingParameterPolygon>(drawings, DrawingType.Polygon, renderSkipUuids)
              .filter(
                (drawing) =>
                  !drawing.drawing_parameters.subtype || drawing.drawing_parameters.subtype === PolygonSubtype.Polygon,
              )
              .map<PolygonPlaneMeshProps>((plane) => {
                const {
                  drawing_parameters: { uuid, points, vertices, center, transformation, styles, order },
                } = plane

                const isEditing = editingElementId === uuid

                return {
                  id: uuid,
                  polygon: {
                    shape_id: `drawing-polygon-${uuid}`,
                    positions: points,
                    vertices,
                    center,
                    transformation,
                    min_rect_boundaries: generateDummyMinimumRectBoundary(vertices),
                  } as Polygon,
                  offsetTransformation: calculateOffsetMatrix((order ?? 1) + 1).toArray(), // Offset by order for layering
                  transparent: true,
                  selectable: isSelectable(uuid) && isAllowedToModify,
                  movable: isSelectable(uuid) && isAllowedToModify,
                  isOpenLoop: true,
                  isEditing,
                  isAdjustingColors: isEditing || selectedElementIds.includes(uuid),
                  visible: !hiddenElementIds.includes(uuid) && mode !== Mode.Editor3D,
                  color: styles?.fillColor || 'transparent',
                  guideLine: {
                    style: styles?.strokeStyle || DEFAULT_STYLES.strokeStyle,
                    color: styles?.strokeColor,
                    thickness: convertPrintDimension(styles?.strokeWidth || 1, currentPrintScale),
                  },
                  vertexAnchors: isAllowedToModify
                    ? {
                        showOnHover: true,
                        onUpdated: (pts) => {
                          const ptsV3 = pts.map((p) => new Vector3(...p))
                          const { vertices: updatedVertices, matrix: updatedMatrix } = createPolygonProjection(ptsV3)
                          const ctr = findCenter(ptsV3)!

                          dispatch(
                            updateDrawingParameters({
                              uuid: editingElementId,
                              points: pts,
                              vertices: updatedVertices,
                              center: ctr.toArray(),
                              transformation: updatedMatrix.toArray(),
                            }),
                          )
                          dispatch(toggleSelectedElementIds({ ids: [uuid], selected: true }))
                        },
                      }
                    : undefined,
                }
              }),
          [
            hiddenElementIds,
            editingElementId,
            selectedElementIds,
            drawings,
            renderSkipUuids,
            mode,
            currentPrintScale,
            isAllowedToModify,
            calculateOffsetMatrix,
            isSelectable,
            dispatch,
          ],
        ),

        /**
         * Working plane
         */
        ...useMemo((): PolygonPlaneMeshProps[] => {
          if (!workingPoints.length) return []

          // Generate vertices once we actually have a triangle.
          // We still need to generate vertices even if we have only 2 points, but function expects at least 3 points.
          // The proper third point will be added in PolygonPlaneMesh.tsx.
          const vertexPoints = workingPoints.length === 2 ? [...workingPoints, [0, 0, 0]] : workingPoints
          const { vertices, center } =
            vertexPoints.length >= 3
              ? getPolygonFromPoints(vertexPoints.map((v) => new Vector3(...v)))
              : { vertices: [], center: findCenter(workingPoints.map((v) => new Vector3(...v)))! }

          return [
            {
              id: WORKING_ID,
              polygon: {
                shape_id: WORKING_ID,
                positions: workingPoints,
                vertices,
                center: center.toArray(),
                transformation: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
                min_rect_boundaries: generateDummyMinimumRectBoundary(vertices as [number, number][]),
              } as Polygon,
              offsetTransformation: calculateOffsetMatrix(drawings.length + 1).toArray(), // Working item is always on top
              transparent: true,
              color: workingStyles?.fillColor || defaultStyles?.fillColor || 'transparent',
              guideLine: {
                style: workingStyles?.strokeStyle || defaultStyles?.strokeStyle,
                color: workingStyles?.strokeColor || defaultStyles?.strokeColor,
                thickness: convertPrintDimension(
                  workingStyles?.strokeWidth ?? defaultStyles?.strokeWidth ?? 1,
                  currentPrintScale,
                ),
              },
              guideLabel: {
                backgroundColor: 'white',
                color: 'black',
                hideLastLabel: isClosing,
              },
              opacity: STANDALONE_PLANE_OPACITY.STANDARD,
              selectable: false,
              isOpenLoop: true,
              isWorking: true,
              isAdjustingColors: true,
            },
          ]
        }, [
          workingPoints,
          workingStyles,
          defaultStyles,
          currentPrintScale,
          isClosing,
          drawings.length,
          calculateOffsetMatrix,
        ]),
      ],
    },
    events: {
      onMouseUp: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, extra: CanvasEventsExtra) => {
        if (!isSelectedTool) return
        const point = getIntersection(e)
        onUp(point, extra)
      },
      onUndo: () => {
        if (!isSelectedTool) return

        handleUndo()
      },
      onCancel: () => {
        if (!isSelectedTool) return

        dispatch(reset())
        dispatch(setWorkingElementId(undefined))
      },
    },
  }
}
